import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, onSnapshot, addDoc, updateDoc } from 'firebase/firestore';
import {
  Home,
  Users,
  Ticket,
  CreditCard,
  Gift,
  Cloud,
  ArrowRightLeft,
  Calendar,
  Settings,
  User,
  List,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Sun,
  Moon,
  Star,
  RefreshCcw,
  AlignLeft,
  ChevronDown,
  ChevronUp,
  X,
  Plus,
  Pencil,
  Key,
  Database,
  Banknote,
  Copy,
  Eye,
  EyeOff,
  Menu,
  Trash2,
  ChevronRight as ChevronRightIcon,
  RotateCcw,
  Square,
  BadgeInfo,
  Ratio,
  Palette,
  LogOut,
  History,
} from 'lucide-react';
import { getAuth, signInWithCustomToken, signInAnonymously } from 'firebase/auth';

// Main App component
const Dashboard = () => {
  const [activeMenuItem, setActiveMenuItem] = useState('User Level');
  const [activeTab, setActiveTab] = useState('User Level');
  const [openTabs, setOpenTabs] = useState(['User Level']);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [userId, setUserId] = useState(null);

  const [memberData, setMemberData] = useState([]);
  const [rewardData, setRewardData] = useState([]);
  const [creditData, setCreditData] = useState([]);
  const [voucherData, setVoucherData] = useState([]);
  const [rekapTransaksiData, setRekapTransaksiData] = useState([]);
  const [rekapMemberData, setRekapMemberData] = useState([]);
  const [rekapRewardData, setRekapRewardData] = useState([]);
  const [rekapOperatorData, setRekapOperatorData] = useState([]);
  const [userLevelData, setUserLevelData] = useState([]);
  const [userManagerData, setUserManagerData] = useState([]);
  const [userLogData, setUserLogData] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;
  const totalRecords = 100; // This will be dynamic with real data
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedRewards, setSelectedRewards] = useState([]);
  const [selectedUserLevels, setSelectedUserLevels] = useState([]);
  const [selectedUserManagers, setSelectedUserManagers] = useState([]);
  const [openModal, setOpenModal] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
  const [isDeleteRewardConfirming, setIsDeleteRewardConfirming] = useState(false);
  const [openSyncModal, setOpenSyncModal] = useState(false);
  const [syncStep, setSyncStep] = useState('Start');
  const [openDropdown, setOpenDropdown] = useState('Administrasi');

  // Firebase initialization and authentication
  useEffect(() => {
    const initializeFirebase = async () => {
      try {
        if (!process.env.REACT_APP_FIREBASE_CONFIG) {
          throw new Error('Firebase config is not defined in environment variables.');
        }
        const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG);
        const app = initializeApp(firebaseConfig);
        const firestore = getFirestore(app);
        const authInstance = getAuth(app);

        if (process.env.REACT_APP_INITIAL_AUTH_TOKEN) {
            await signInWithCustomToken(authInstance, process.env.REACT_APP_INITIAL_AUTH_TOKEN);
        } else {
            await signInAnonymously(authInstance);
        }
        
        setDb(firestore);
        setAuth(authInstance);
        setUserId(authInstance.currentUser?.uid || crypto.randomUUID());
      } catch (e) {
        console.error("Firebase initialization error:", e);
      }
    };

    initializeFirebase();

    // Placeholder data for other tables (to be replaced with Firestore in future steps)
    setCreditData([
      { id: 1, noTanggal: '18/08/2025 03:12 PM', memberId: 'MEMBER1', kategori: 'Play', credit: '(1)', voucher: 'VOUCHER011FDCC9FB1499EAAC8449E8C6AB741', keterangan: 'Anda menang IPHONE 16 PRO', operator: '18/08/2025 02:10:46 PM by Member' },
      { id: 2, noTanggal: '18/08/2025 03:09 PM', memberId: 'MEMBER1', kategori: 'Play', credit: '(1)', voucher: 'VOUCHER3C34B46EBA408B6AF934098F9FF9', keterangan: 'Anda belum beruntung', operator: '18/08/2025 03:09:21 PM by Member' },
    ]);
    setVoucherData([
      { id: 1, no: 1, voucher: 'VOUCHER2B9D69C5C01F499EAAC8449E8C6AB741', status: 'Available', digunakanTgl: '', operator: '18/08/2025 02:10:45 PM by Seeder' },
    ]);
    setRekapTransaksiData([
      { id: 1, no: 1, tanggal: '18/08/2025', member: '100', deposit: '6,164', withdraw: 0, spin: '(2)', credit: '6,162', voucherIn: 500, voucherOut: 498 },
    ]);
    setRekapMemberData([
      { id: 1, no: 1, tanggal: '18/08/2025', memberId: 'MEMBER100', deposit: 31, withdraw: 0, spin: 0, credit: 31, voucherIn: 5, voucherOut: 5 },
    ]);
    setRekapRewardData([
      { id: 1, no: 1, tanggal: '18/08/2025', reward: 'IPHONE 16 PRO', memberId: 'MEMBER1', win: 1 },
      { id: 2, no: 2, tanggal: '18/08/2025', reward: 'ZONK', memberId: 'MEMBER1', win: 2 },
    ]);
    setRekapOperatorData([
      { id: 1, no: 1, tanggal: '18/08/2025', operator: 'MEMBER86A1F...', deposit: 0, withdraw: 0, creditIn: 0, voucherIn: 0, voucherCancelled: 0, voucher: 0 },
      { id: 2, no: 2, tanggal: '18/08/2025', operator: 'SEEDER', deposit: '6,164', withdraw: 0, creditIn: '6,164', voucherIn: 500, voucherCancelled: 0, voucher: 500 },
    ]);
    setUserLevelData([
      { id: 1, no: 1, namaLevel: 'Administrator', keterangan: 'Super Admin', operator: '18/08/2025 07:50:25 AM by Seeder' },
    ]);
    setUserManagerData([
      { id: 1, no: 1, username: 'administrator', namaLengkap: 'Administrator', level: 'Administrator', status: 'Enabled', ipWhitelist: '110.74.197.161', login: '18/08/2025 10:48:53 PM' },
    ]);
    setUserLogData([
      { id: 1, no: 1, waktu: '18/08/2025 02:54:19 PM', username: 'administrator', ipAddress: '128.199.136.136', platform: 'Mac OS X', browser: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit...' },
      { id: 2, no: 2, waktu: '18/08/2025 03:59:14 PM', username: 'administrator', ipAddress: '110.74.197.161', platform: 'Windows 10', browser: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit...' },
    ]);
  }, []);

  // Effect for fetching member data from Firestore in real-time
  useEffect(() => {
    // Only proceed if Firebase is initialized and we have a user ID
    if (!db || !userId) {
      return;
    }

    const appId = process.env.REACT_APP_ID;
    if (!appId) {
      console.error('Firebase app ID is not defined in environment variables.');
      // You might want to set an error state here to show in the UI
      return;
    }
    try {
      const memberCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/members`);
      
      const unsubscribe = onSnapshot(memberCollectionRef, (querySnapshot) => {
        const fetchedMembers = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMemberData(fetchedMembers);
      });

      // Clean up the listener when the component unmounts
      return () => unsubscribe();
    } catch (e) {
      console.error("Error fetching member data:", e);
    }
  }, [db, userId]);

  // Effect for fetching reward data from Firestore in real-time
  useEffect(() => {
    if (!db || !userId) {
      return;
    }

    const appId = process.env.REACT_APP_ID;
    if (!appId) {
      console.error('Firebase app ID is not defined in environment variables.');
      // You might want to set an error state here to show in the UI
      return;
    }
    try {
      const rewardCollectionRef = collection(db, `artifacts/${appId}/users/${userId}/rewards`);
      
      const unsubscribe = onSnapshot(rewardCollectionRef, (querySnapshot) => {
        const fetchedRewards = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRewardData(fetchedRewards);
      });

      return () => unsubscribe();
    } catch (e) {
      console.error("Error fetching reward data:", e);
    }
  }, [db, userId]);

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  const handleOpenModal = (modalType) => {
    if (['Password', 'Deposit', 'Withdraw', 'Voucher'].includes(modalType) && selectedMembers.length !== 1) {
      alert(`Please select a single member to ${modalType.toLowerCase()}.`);
      return;
    }
    if (['CreateReward', 'EditReward', 'RatioReward', 'ColorReward'].includes(modalType) && selectedRewards.length === 0) {
      alert(`Please select a reward to edit.`);
      return;
    }
    setOpenModal(modalType);
  };

  const handleModalClose = () => {
    setOpenModal(null);
    setSelectedMembers([]);
    setSelectedRewards([]);
    setSelectedUserLevels([]);
    setSelectedUserManagers([]);
    setIsConfirming(false);
    setIsDeleteConfirming(false);
    setIsDeleteRewardConfirming(false);
    setOpenSyncModal(false);
    setSyncStep('Start');
  };

  // Function to handle member form submission (Create and Edit)
  const handleMemberFormSubmit = async (formData) => {
    try {
      const appId = process.env.REACT_APP_ID;
      if (selectedMembers.length === 1) {
        // Edit mode
        const memberRef = doc(db, `artifacts/${appId}/users/${userId}/members`, selectedMembers[0]);
        await setDoc(memberRef, formData, { merge: true }); // Use setDoc with merge to update fields
      } else {
        // Create mode
        await addDoc(collection(db, `artifacts/${appId}/users/${userId}/members`), formData);
      }
      handleModalClose();
    } catch (e) {
      console.error("Error saving member:", e);
      alert("Error saving member. Please try again.");
    }
  };

  // Function to handle deleting members
  const handleDeleteMembers = async () => {
    if (!db || !userId) return;
    const appId = process.env.REACT_APP_ID;
    try {
      const deletePromises = selectedMembers.map(memberId =>
        deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/members`, memberId))
      );
      await Promise.all(deletePromises);
      setIsDeleteConfirming(false);
      setSelectedMembers([]);
      alert(`Successfully deleted ${selectedMembers.length} member(s).`);
    } catch (e) {
      console.error("Error deleting members:", e);
      alert("Error deleting members. Please try again.");
    }
  };

  // Function to handle reward form submission (Create and Edit)
  const handleRewardFormSubmit = async (formData) => {
    if (!db || !userId) return;
    const appId = process.env.REACT_APP_ID;
    try {
        if (selectedRewards.length === 1 && (openModal === 'EditReward' || openModal === 'RatioReward' || openModal === 'ColorReward')) {
            // Edit mode for a single reward
            const rewardRef = doc(db, `artifacts/${appId}/users/${userId}/rewards`, selectedRewards[0]);
            await updateDoc(rewardRef, formData);
        } else if (openModal === 'CreateReward') {
            // Create mode for a new reward
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/rewards`), formData);
        }
        handleModalClose();
    } catch (e) {
        console.error("Error saving reward:", e);
        alert("Error saving reward. Please try again.");
    }
  };

  // Function to handle deleting rewards
  const handleDeleteRewards = async () => {
    if (!db || !userId) return;
    const appId = process.env.REACT_APP_ID;
    try {
      const deletePromises = selectedRewards.map(rewardId =>
        deleteDoc(doc(db, `artifacts/${appId}/users/${userId}/rewards`, rewardId))
      );
      await Promise.all(deletePromises);
      setIsDeleteRewardConfirming(false);
      setSelectedRewards([]);
      alert(`Successfully deleted ${selectedRewards.length} reward(s).`);
    } catch (e) {
      console.error("Error deleting rewards:", e);
      alert("Error deleting rewards. Please try again.");
    }
  };

  const handleRowSelect = (id, type) => {
    if (type === 'member') {
      const isSelected = selectedMembers.includes(id);
      if (isSelected) {
        setSelectedMembers(selectedMembers.filter((memberId) => memberId !== id));
      } else {
        setSelectedMembers([...selectedMembers, id]);
      }
    } else if (type === 'reward') {
      const isSelected = selectedRewards.includes(id);
      if (isSelected) {
        setSelectedRewards(selectedRewards.filter((rewardId) => rewardId !== id));
      } else {
        setSelectedRewards([...selectedRewards, id]);
      }
    } else if (type === 'userLevel') {
      const isSelected = selectedUserLevels.includes(id);
      if (isSelected) {
        setSelectedUserLevels(selectedUserLevels.filter((levelId) => levelId !== id));
      } else {
        setSelectedUserLevels([...selectedUserLevels, id]);
      }
    } else if (type === 'userManager') {
      const isSelected = selectedUserManagers.includes(id);
      if (isSelected) {
        setSelectedUserManagers(selectedUserManagers.filter((managerId) => managerId !== id));
      } else {
        setSelectedUserManagers([...selectedUserManagers, id]);
      }
    }
  };

  const handleSelectAll = (e, type) => {
    if (type === 'member') {
      if (e.target.checked) {
        const allMemberIds = memberData.map(member => member.id);
        setSelectedMembers(allMemberIds);
      } else {
        setSelectedMembers([]);
      }
    } else if (type === 'reward') {
      if (e.target.checked) {
        const allRewardIds = rewardData.map(reward => reward.id);
        setSelectedRewards(allRewardIds);
      } else {
        setSelectedRewards([]);
      }
    } else if (type === 'userLevel') {
      if (e.target.checked) {
        const allLevelIds = userLevelData.map(level => level.id);
        setSelectedUserLevels(allLevelIds);
      } else {
        setSelectedUserLevels([]);
      }
    } else if (type === 'userManager') {
      if (e.target.checked) {
        const allManagerIds = userManagerData.map(manager => manager.id);
        setSelectedUserManagers(allManagerIds);
      } else {
        setSelectedUserManagers([]);
      }
    }
  };

  const handleDepositWithdraw = (amount, type) => {
    const newAmount = parseInt(amount, 10);
    if (isNaN(newAmount) || newAmount <= 0) {
      alert("Please enter a valid amount.");
      return;
    }
    const updatedData = memberData.map(member => {
      if (member.id === selectedMembers[0]) {
        let newDeposit = member.deposit;
        let newWithdraw = member.withdraw;
        if (type === 'deposit') {
          newDeposit += newAmount;
        } else if (type === 'withdraw') {
          newWithdraw += newAmount;
        }
        return {
          ...member,
          deposit: newDeposit,
          withdraw: newWithdraw,
          play: newDeposit - newWithdraw,
        };
      }
      return member;
    });
    setMemberData(updatedData);
    handleModalClose();
  };

  const handleCreateVoucher = () => {
    if (!isConfirming) {
      setIsConfirming(true);
    } else {
      const newVoucher = {
        no: voucherData.length + 1,
        voucher: `VOUCHER${Math.random().toString(36).substring(2, 15).toUpperCase()}`,
        status: 'Available',
        digunakanTgl: '',
        operator: `18/08/2025 09:22:51 PM by Seeder`
      };
      setVoucherData([...voucherData, newVoucher]);
      setIsConfirming(false);
    }
  };

  const syncSteps = ['Start', 'Login', 'OTP', 'Sync'];

  // Sidebar menu items and their corresponding icons
  const menuItems = [
    {
      name: 'Master', icon: <Home size={18} />, subItems: [
        { name: 'Member', icon: <Users size={18} /> },
        { name: 'Voucher', icon: <Ticket size={18} /> },
        { name: 'Credit', icon: <CreditCard size={18} /> },
        { name: 'Reward', icon: <Gift size={18} /> },
        { name: 'Kuota', icon: <Cloud size={18} /> },
        { name: 'Transaksi', icon: <ArrowRightLeft size={18} /> },
        { name: 'Cron', icon: <RefreshCcw size={18} /> },
      ]
    },
    {
      name: 'Laporan', icon: <List size={18} />, subItems: [
        { name: 'Rekap Transaksi', icon: <List size={18} /> },
        { name: 'Rekap Member', icon: <List size={18} /> },
        { name: 'Rekap Reward', icon: <List size={18} /> },
        { name: 'Rekap Operator', icon: <List size={18} /> },
      ]
    },
    {
      name: 'Administrasi', icon: <Settings size={18} />, subItems: [
        { name: 'User Level', icon: <User size={18} /> },
        { name: 'User Manager', icon: <User size={18} /> },
        { name: 'User Log', icon: <List size={18} /> },
      ]
    },
  ];

  // Tabs for the main content area
  const mainTabs = [
    'Member',
    'Voucher',
    'Credit',
    'Reward',
    'Cron',
    'Kuota',
    'Transaksi',
    'Rekap Transaksi',
    'Rekap Member',
    'Rekap Reward',
    'Rekap Operator',
    'User Level',
    'User Manager',
    'User Log',
    'Dashboard',
  ];

  // Sidebar component
  const Sidebar = () => (
    <aside className={`fixed md:relative top-0 left-0 h-full w-64 md:w-64 bg-slate-900 text-slate-300 p-4 flex-col rounded-l-xl transition-transform transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 dark:bg-gray-900 dark:text-gray-300 z-50`}>
      <div className="flex items-center space-x-2 py-4 px-2 mb-4">
        <Star size={24} className="text-yellow-400" />
        <span className="font-bold text-lg">SpinToWin v1.0.3</span>
      </div>
      <nav className="flex-1 overflow-y-auto">
        {menuItems.map((item) => (
          <div key={item.name}>
            <button
              onClick={() => {
                setOpenDropdown(openDropdown === item.name ? null : item.name);
                setActiveMenuItem(item.name);
              }}
              className={`w-full flex items-center p-2 rounded-lg transition-colors duration-200 font-bold mt-4 ${
                openDropdown === item.name
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'hover:bg-slate-700 dark:hover:bg-gray-800'
              }`}
            >
              {item.icon}
              <span className="ml-3">{item.name}</span>
              <span className="ml-auto">
                {openDropdown === item.name ? <ChevronDown size={16} /> : <ChevronRightIcon size={16} />}
              </span>
            </button>
            {openDropdown === item.name && (
              <ul className="pl-6 pt-2 space-y-2">
                {item.subItems.map(subItem => (
                  <li key={subItem.name}>
                    <button
                      onClick={() => {
                        setActiveTab(subItem.name);
                        if (!openTabs.includes(subItem.name)) {
                          setOpenTabs([...openTabs, subItem.name]);
                        }
                        setIsSidebarOpen(false); // Close on mobile
                      }}
                      className={`w-full text-left p-2 rounded-lg transition-colors duration-200 ${
                        activeTab === subItem.name
                          ? 'bg-blue-400 text-white shadow-lg'
                          : 'hover:bg-slate-700 dark:hover:bg-gray-800'
                      }`}
                    >
                      {subItem.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </nav>
      <button
        onClick={() => setIsSidebarOpen(false)}
        className="absolute top-4 right-4 md:hidden text-white"
      >
        <X size={24} />
      </button>
    </aside>
  );

  // Header component
  const Header = () => {
    const handleSettingsClick = () => {
      // Logic to open the User Level tab
      setActiveTab('User Level');
      setOpenDropdown('Administrasi');
      setActiveMenuItem('Administrasi');
      if (!openTabs.includes('User Level')) {
        setOpenTabs([...openTabs, 'User Level']);
      }
    };
    return (
      <header className="flex items-center justify-between p-4 bg-white text-slate-700 shadow-md rounded-t-xl dark:bg-gray-800 dark:text-gray-200">
        <div className="flex items-center space-x-2 md:space-x-4 text-sm font-medium">
          <button onClick={() => setIsSidebarOpen(true)} className="md:hidden">
            <Menu size={24} />
          </button>
          <span className="hidden md:block">110.74.197.161, GMT+7</span>
          <span className="hidden md:block">Senin, 18 Agu 2025 08:10:09 PM</span>
        </div>
        <div className="flex-1 text-center">
          <h1 className="text-lg md:text-xl font-bold">VOUCHER 1</h1>
        </div>
        <div className="flex items-center space-x-2 md:space-x-4">
          <button onClick={toggleDarkMode} className="text-slate-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 transition-colors">
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <select className="bg-slate-100 rounded-lg py-1 px-2 border border-slate-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
            <option>en</option>
          </select>
          <span className="flex items-center space-x-1">
            <User size={18} />
            <span className="hidden md:block">Administrator</span>
          </span>
          <button onClick={handleSettingsClick} className="text-slate-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 transition-colors">
            <Settings size={18} />
          </button>
        </div>
      </header>
    );
  };

  // General Modal component
  const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto p-6 dark:bg-gray-800 dark:text-gray-200">
          <div className="flex justify-between items-center border-b pb-3 mb-4 dark:border-gray-700">
            <h2 className="text-xl font-bold">{title}</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300">
              <X size={24} />
            </button>
          </div>
          {children}
        </div>
      </div>
    );
  };

  // Member Modal for a single member
  const MemberModal = () => {
    const isEditMode = selectedMembers.length === 1;
    const initialMember = isEditMode ? memberData.find(member => member.id === selectedMembers[0]) : {};
    const [formData, setFormData] = useState(initialMember || { memberId: '', password: '', name: '', status: 'Enabled', keterangan: '', deposit: 0, withdraw: 0, play: 0 });

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      await handleMemberFormSubmit(formData);
    };

    return (
      <Modal isOpen={openModal === 'Create' || openModal === 'Edit' && selectedMembers.length === 1} onClose={handleModalClose} title={isEditMode ? 'Edit' : 'Create'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Member ID</label>
            <input type="text" name="memberId" value={formData.memberId} onChange={handleChange} disabled={isEditMode} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50" required />
          </div>
          {!isEditMode && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Password</label>
              <input type="password" name="password" value={formData.password} onChange={handleChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Nama Member</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Status</label>
            <div className="flex space-x-4 mt-2">
              <label className="flex items-center space-x-2">
                <input type="radio" name="status" value="Enabled" checked={formData.status === 'Enabled'} onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Enabled</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" name="status" value="Disabled" checked={formData.status === 'Disabled'} onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Disabled</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Keterangan</label>
            <textarea name="keterangan" value={formData.keterangan || ''} onChange={handleChange} rows="3" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"></textarea>
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
            <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
              {isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    );
  };

  // Batch Edit Modal
  const BatchEditModal = () => {
    const [formData, setFormData] = useState({ status: '', keterangan: '' });

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      handleMemberFormSubmit(formData);
    };

    return (
      <Modal isOpen={openModal === 'Edit' && selectedMembers.length > 1} onClose={handleModalClose} title="Batch Edit Members">
        <p className="text-sm text-slate-500 mb-4 dark:text-gray-400">Editing {selectedMembers.length} selected members.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Status</label>
            <div className="flex space-x-4 mt-2">
              <label className="flex items-center space-x-2">
                <input type="radio" name="status" value="Enabled" onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Enabled</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" name="status" value="Disabled" onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Disabled</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Keterangan</label>
            <textarea name="keterangan" value={formData.keterangan} onChange={handleChange} rows="3" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"></textarea>
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
            <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
              Apply to All
            </button>
          </div>
        </form>
      </Modal>
    );
  };

  // Reward Modals
  const RewardCreateEditModal = () => {
    const isEditMode = selectedRewards.length === 1;
    const initialReward = isEditMode ? rewardData.find(reward => reward.id === selectedRewards[0]) : {};
    const [formData, setFormData] = useState(initialReward || { winLose: 'Win', status: 'Enabled' });

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      handleRewardFormSubmit(formData);
    };

    return (
      <Modal isOpen={openModal === 'CreateReward' || (openModal === 'EditReward' && selectedRewards.length === 1)} onClose={handleModalClose} title={isEditMode ? 'Edit Reward' : 'Create Reward'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Nama Reward</label>
            <input type="text" name="name" value={formData.name || ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Win/Lose</label>
            <div className="flex space-x-4 mt-2">
              <label className="flex items-center space-x-2">
                <input type="radio" name="winLose" value="Win" checked={formData.winLose === 'Win'} onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Win</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" name="winLose" value="Lose" checked={formData.winLose === 'Lose'} onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Lose</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Status</label>
            <div className="flex space-x-4 mt-2">
              <label className="flex items-center space-x-2">
                <input type="radio" name="status" value="Enabled" checked={formData.status === 'Enabled'} onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Enabled</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="radio" name="status" value="Disabled" checked={formData.status === 'Disabled'} onChange={handleChange} className="form-radio text-blue-500" />
                <span className="text-slate-700 dark:text-gray-200">Disabled</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Keterangan</label>
            <textarea name="keterangan" value={formData.keterangan || ''} onChange={handleChange} rows="3" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"></textarea>
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
            <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
              {isEditMode ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    );
  };

  const RewardRatioModal = () => {
    const selectedReward = rewardData.find(r => r.id === selectedRewards[0]);
    const [ratio, setRatio] = useState(selectedReward?.ratio || '');

    const handleSubmit = async (e) => {
      e.preventDefault();
      await handleRewardFormSubmit({ ratio: parseInt(ratio, 10) });
    };

    return (
      <Modal isOpen={openModal === 'RatioReward'} onClose={handleModalClose} title="Ratio">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Nama Reward</label>
            <input type="text" value={selectedReward?.name || ''} disabled className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Ratio</label>
            <input type="number" name="ratio" value={ratio} onChange={(e) => setRatio(e.target.value)} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
            <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
              Save
            </button>
          </div>
        </form>
      </Modal>
    );
  };

  const RewardColorModal = () => {
    const selectedReward = rewardData.find(r => r.id === selectedRewards[0]);
    const [colors, setColors] = useState({
      background: selectedReward?.background || '#000000',
      color: selectedReward?.color || '#FFFFFF',
    });

    const handleChange = (e) => {
      const { name, value } = e.target;
      setColors((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      await handleRewardFormSubmit(colors);
    };

    return (
      <Modal isOpen={openModal === 'ColorReward'} onClose={handleModalClose} title="Color">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Nama Reward</label>
            <input type="text" value={selectedReward?.name || ''} disabled className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Background</label>
            <input type="color" name="background" value={colors.background} onChange={handleChange} className="mt-1 block w-full h-10 rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Color</label>
            <input type="color" name="color" value={colors.color} onChange={handleChange} className="mt-1 block w-full h-10 rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600" required />
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
            <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
              Save
            </button>
          </div>
        </form>
      </Modal>
    );
  };


  // Password Modal
  const PasswordModal = () => {
    const [passwordData, setPasswordData] = useState({ newPassword: '', retypePassword: '' });
    const [showPassword, setShowPassword] = useState(false);

    const handlePasswordChange = (e) => {
      const { name, value } = e.target;
      setPasswordData({ ...passwordData, [name]: value });
    };

    const handlePasswordSubmit = (e) => {
      e.preventDefault();
      if (passwordData.newPassword !== passwordData.retypePassword) {
        alert("Passwords do not match.");
        return;
      }
      alert(`Password for ${memberData.find(member => member.id === selectedMembers[0])?.name} updated successfully!`);
      handleModalClose();
    };

    return (
      <Modal isOpen={openModal === 'Password'} onClose={handleModalClose} title="Password">
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Member ID</label>
            <input type="text" value={memberData.find(member => member.id === selectedMembers[0])?.memberId || ''} disabled className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Password Baru</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm pr-10 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 dark:text-gray-500">
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Ketik Ulang Password</label>
            <div className="relative">
              <input type={showPassword ? "text" : "password"} name="retypePassword" value={passwordData.retypePassword} onChange={handlePasswordChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm pr-10 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 dark:text-gray-500">
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
            <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
              Save
            </button>
          </div>
        </form>
      </Modal>
    );
  };

  // Deposit/Withdraw Modal
  const DepositWithdrawModal = ({ type }) => {
    const [amount, setAmount] = useState('');

    const handleSubmit = (e) => {
      e.preventDefault();
      handleDepositWithdraw(amount, type);
    };

    return (
      <Modal isOpen={openModal === type} onClose={handleModalClose} title={type === 'deposit' ? 'Deposit' : 'Withdraw'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Member ID</label>
            <input type="text" value={memberData.find(member => member.id === selectedMembers[0])?.memberId || ''} disabled className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">{type === 'deposit' ? 'Deposit' : 'Withdraw'}</label>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
          </div>
          <div className="flex justify-end space-x-2">
            <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
            <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
              Save
            </button>
          </div>
        </form>
      </Modal>
    );
  };

  // Voucher Modal
  const VoucherModal = () => {
    const copyToClipboard = (text) => {
      const tempInput = document.createElement('input');
      tempInput.value = text;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand('copy');
      document.body.removeChild(tempInput);
      alert('Voucher copied to clipboard!');
    };

    return (
      <Modal isOpen={openModal === 'Voucher'} onClose={handleModalClose} title="Voucher">
        <div className="flex justify-between items-center mb-4">
          <div className="flex space-x-2">
            <button onClick={handleCreateVoucher} className="bg-green-500 text-white py-2 px-4 rounded-lg flex items-center hover:bg-green-600 transition-colors duration-200">
              <Plus size={16} className="mr-2" /> Create
            </button>
            <button onClick={handleModalClose} className="bg-slate-200 text-slate-700 py-2 px-4 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              Cancel
            </button>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
          <table className="min-w-full bg-white dark:bg-gray-800">
            <thead className="bg-blue-500 text-white">
              <tr>
                <th className="py-3 px-4 text-left font-semibold">No</th>
                <th className="py-3 px-4 text-left font-semibold">Voucher</th>
                <th className="py-3 px-4 text-left font-semibold">Operator</th>
              </tr>
            </thead>
            <tbody className="text-slate-700 dark:text-gray-200">
              {voucherData.length > 0 ? (
                voucherData.map((voucher) => (
                  <tr key={voucher.no} className="border-b border-slate-200 dark:border-gray-700">
                    <td className="py-2 px-4">{voucher.no}</td>
                    <td className="py-2 px-4 flex items-center space-x-2">
                      <span>{voucher.voucher}</span>
                      <button onClick={() => copyToClipboard(voucher.voucher)} className="text-blue-500 hover:text-blue-700 transition-colors">
                        <Copy size={16} />
                      </button>
                    </td>
                    <td className="py-2 px-4">{voucher.operator}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="py-8 text-center text-slate-400">
                    No vouchers generated yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {isConfirming && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 text-center dark:bg-gray-800 dark:text-gray-200">
              <h3 className="font-bold text-lg mb-4">Voucher</h3>
              <p className="text-slate-700 dark:text-gray-300 mb-6">Apakah anda yakin ingin proses voucher ?</p>
              <div className="flex justify-center space-x-4">
                <button onClick={() => { setIsConfirming(false); handleCreateVoucher(); }} className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors">
                  Yes
                </button>
                <button onClick={() => setIsConfirming(false)} className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    );
  };

  // Sync Modal Component
  const SyncModal = () => {
    const handleNextStep = () => {
      const currentStepIndex = syncSteps.indexOf(syncStep);
      if (currentStepIndex < syncSteps.length - 1) {
        setSyncStep(syncSteps[currentStepIndex + 1]);
      }
    };
    
    return (
      <Modal isOpen={openSyncModal} onClose={handleModalClose} title="Sinkron">
        <div className="flex justify-center space-x-2 mb-6">
          {syncSteps.map((step, index) => (
            <div key={step} className="flex items-center">
              <span className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors duration-200 ${syncStep === step ? 'bg-blue-500 text-white' : 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                {step}
              </span>
              {index < syncSteps.length - 1 && (
                <ChevronRightIcon size={20} className="text-slate-400 dark:text-gray-500" />
              )}
            </div>
          ))}
        </div>
        <div className="p-4 bg-slate-100 rounded-lg dark:bg-gray-700 space-y-4">
          {syncStep === 'Start' && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Min Depo :</label>
                <input type="number" defaultValue="100000" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Tgl Sinkron :</label>
                <input type="date" defaultValue="2025-08-17" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" />
              </div>
              <div className="flex justify-end mt-4">
                <button onClick={handleNextStep} className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-600 transition-colors">
                  Lanjut
                  <ChevronRightIcon size={16} className="ml-2" />
                </button>
              </div>
            </>
          )}
          {syncStep === 'Login' && (
            <div className="text-center text-slate-400">
              Login to be implemented.
            </div>
          )}
          {syncStep === 'OTP' && (
            <div className="text-center text-slate-400">
              OTP verification to be implemented.
            </div>
          )}
          {syncStep === 'Sync' && (
            <div className="text-center text-slate-400">
              Syncing process to be implemented.
            </div>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
            Selesai
          </button>
        </div>
      </Modal>
    );
  };

  // Delete Confirmation Modals
  const DeleteConfirmationModal = () => {
    return (
      <Modal isOpen={isDeleteConfirming} onClose={() => setIsDeleteConfirming(false)} title="Delete Member">
        <div className="text-center">
          <Trash2 size={48} className="mx-auto text-red-500 mb-4" />
          <p className="text-slate-700 dark:text-gray-300 mb-6">Are you sure you want to delete {selectedMembers.length} member(s)? This action cannot be undone.</p>
          <div className="flex justify-center space-x-4">
            <button onClick={() => setIsDeleteConfirming(false)} className="bg-slate-200 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              No
            </button>
            <button onClick={handleDeleteMembers} className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors">
              Yes, Delete
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  const DeleteRewardConfirmationModal = () => {
    return (
      <Modal isOpen={isDeleteRewardConfirming} onClose={() => setIsDeleteRewardConfirming(false)} title="Delete Reward">
        <div className="text-center">
          <Trash2 size={48} className="mx-auto text-red-500 mb-4" />
          <p className="text-slate-700 dark:text-gray-300 mb-6">Are you sure you want to delete {selectedRewards.length} reward(s)? This action cannot be undone.</p>
          <div className="flex justify-center space-x-4">
            <button onClick={() => setIsDeleteRewardConfirming(false)} className="bg-slate-200 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
              No
            </button>
            <button onClick={handleDeleteRewards} className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors">
              Yes, Delete
            </button>
          </div>
        </div>
      </Modal>
    );
  };

  // Main content area
  const MainContent = () => {
    // --- Shared functions for all tabs
    const handleSearch = () => {
      alert('Search button clicked!');
    };

    const handleDateChange = (dateType) => (e) => {
      console.log(`${dateType} date changed to:`, e.target.value);
    };
    
    const handleCloseTab = (tabToClose) => {
      const newOpenTabs = openTabs.filter(tab => tab !== tabToClose);
      if (newOpenTabs.length > 0) {
        setActiveTab(newOpenTabs[newOpenTabs.length - 1]);
      } else {
        setActiveTab('');
      }
      setOpenTabs(newOpenTabs);
    };

    const commonPagination = (currentTableData, totalRecords) => {
      const totalPages = Math.ceil(totalRecords / itemsPerPage);
      return (
        <div className="flex flex-col md:flex-row items-center justify-between mt-4">
          <span className="text-sm text-slate-500 dark:text-gray-400 mb-2 md:mb-0">
            Showing {currentTableData.length > 0 ? 1 : 0} - {currentTableData.length} of {totalRecords} records
          </span>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="text-slate-500 disabled:text-slate-300 dark:text-gray-400 dark:disabled:text-gray-600"
            >
              <ChevronsLeft size={20} />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="text-slate-500 disabled:text-slate-300 dark:text-gray-400 dark:disabled:text-gray-600"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-500 dark:text-gray-400">Page</span>
              <input
                type="number"
                value={currentPage}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!isNaN(value)) {
                    setCurrentPage(value);
                  }
                }}
                className="w-12 text-center border rounded-lg dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
              />
              <span className="text-sm text-slate-500 dark:text-gray-400">of {totalPages}</span>
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="text-slate-500 disabled:text-slate-300 dark:text-gray-400 dark:disabled:text-gray-600"
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="text-slate-500 disabled:text-slate-300 dark:text-gray-400 dark:disabled:text-gray-600"
            >
              <ChevronsRight size={20} />
            </button>
            <button className="bg-slate-200 py-1 px-3 rounded-lg text-sm text-slate-600 hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500">
              GO
            </button>
          </div>
        </div>
      );
    };

    const memberTabContent = () => {
      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => handleOpenModal('Create')} className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center">
                <Plus size={16} className="mr-2" /> Create
              </button>
              <button onClick={() => handleOpenModal('Edit')} disabled={selectedMembers.length === 0} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Pencil size={16} className="mr-2" /> Edit
              </button>
              <button onClick={() => handleOpenModal('Password')} disabled={selectedMembers.length !== 1} className="bg-yellow-500 text-white py-2 px-4 rounded-lg hover:bg-yellow-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Key size={16} className="mr-2" /> Password
              </button>
              <button onClick={() => handleOpenModal('deposit')} disabled={selectedMembers.length !== 1} className="bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Database size={16} className="mr-2" /> Deposit
              </button>
              <button onClick={() => handleOpenModal('withdraw')} disabled={selectedMembers.length !== 1} className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Banknote size={16} className="mr-2" /> Withdraw
              </button>
              <button onClick={() => handleOpenModal('Voucher')} disabled={selectedMembers.length !== 1} className="bg-indigo-500 text-white py-2 px-4 rounded-lg hover:bg-indigo-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Ticket size={16} className="mr-2" /> Voucher
              </button>
              <button onClick={() => setIsDeleteConfirming(true)} disabled={selectedMembers.length === 0} className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Trash2 size={16} className="mr-2" /> Delete
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <input type="text" placeholder="Member ID" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
              <select className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option>-Status-</option> <option>Enabled</option> <option>Disabled</option>
              </select>
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-05" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold">
                    <input type="checkbox" checked={selectedMembers.length === memberData.length && memberData.length > 0} onChange={(e) => handleSelectAll(e, 'member')} className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                  </th>
                  <th className="py-3 px-4 text-left font-semibold">Member ID</th>
                  <th className="py-3 px-4 text-left font-semibold">Nama Member</th>
                  <th className="py-3 px-4 text-left font-semibold">Status</th>
                  <th className="py-3 px-4 text-left font-semibold">Keterangan</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {memberData.length > 0 ? (
                  memberData.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowSelect(row.id, 'member')}
                      className={`border-b border-slate-200 transition-colors duration-200 cursor-pointer ${
                        selectedMembers.includes(row.id)
                          ? 'bg-blue-100 dark:bg-blue-900'
                          : 'hover:bg-slate-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <td className="py-2 px-4">
                        <input type="checkbox" checked={selectedMembers.includes(row.id)} readOnly className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                      </td>
                      <td className="py-2 px-4">{row.memberId}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.name}</td>
                      <td className="py-2 px-4">
                        <span className="bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                          {row.status}
                        </span>
                      </td>
                      <td className="py-2 px-4">{row.keterangan}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(memberData, memberData.length)}
        </div>
      );
    };

    const voucherTabContent = () => {
      const copyToClipboard = (text) => {
        const tempInput = document.createElement('input');
        tempInput.value = text;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        alert('Voucher copied to clipboard!');
      };

      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex flex-wrap gap-2">
              <button className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center">
                <Plus size={16} className="mr-2" /> Create
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <input type="text" placeholder="Member ID" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
              <select className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option>-Status-</option> <option>Available</option> <option>Used</option>
              </select>
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-05" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                  <th className="py-3 px-4 text-left font-semibold">Voucher</th>
                  <th className="py-3 px-4 text-left font-semibold">Status</th>
                  <th className="py-3 px-4 text-left font-semibold">Digunakan Tgl</th>
                  <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Operator</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {voucherData.length > 0 ? (
                  voucherData.map((row) => (
                    <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                      <td className="py-2 px-4">{row.no}</td>
                      <td className="py-2 px-4 flex items-center space-x-2">
                        <span>{row.voucher}</span>
                        <button onClick={() => copyToClipboard(row.voucher)} className="text-blue-500 hover:text-blue-700 transition-colors">
                          <Copy size={16} />
                        </button>
                      </td>
                      <td className="py-2 px-4">
                        <span className="bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                          {row.status}
                        </span>
                      </td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.digunakanTgl || '-'}</td>
                      <td className="py-2 px-4 text-xs whitespace-nowrap">{row.operator}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(voucherData, 500)}
        </div>
      );
    };

    const creditTabContent = () => {
      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex flex-wrap gap-2">
              {/* No buttons in the credit toolbar from the image */}
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <input type="text" placeholder="Member ID" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
              <select className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option>-Kategori-</option> <option>Play</option> <option>Deposit</option>
              </select>
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-05" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No Tanggal</th>
                  <th className="py-3 px-4 text-left font-semibold">Member ID</th>
                  <th className="py-3 px-4 text-left font-semibold">Kategori</th>
                  <th className="py-3 px-4 text-left font-semibold">Credit</th>
                  <th className="py-3 px-4 text-left font-semibold">Voucher</th>
                  <th className="py-3 px-4 text-left font-semibold">Keterangan</th>
                  <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Operator</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {creditData.length > 0 ? (
                  creditData.map((row) => (
                    <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                      <td className="py-2 px-4 whitespace-nowrap">{row.noTanggal}</td>
                      <td className="py-2 px-4">{row.memberId}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.kategori}</td>
                      <td className="py-2 px-4 text-right">{row.credit}</td>
                      <td className="py-2 px-4">{row.voucher}</td>
                      <td className="py-2 px-4">{row.keterangan}</td>
                      <td className="py-2 px-4 text-xs whitespace-nowrap">{row.operator}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(creditData, 200)}
        </div>
      );
    };

    const rewardTabContent = () => {
      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => handleOpenModal('CreateReward')} className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center">
                <Plus size={16} className="mr-2" /> Create
              </button>
              <button onClick={() => handleOpenModal('EditReward')} disabled={selectedRewards.length === 0} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Pencil size={16} className="mr-2" /> Edit
              </button>
              <button onClick={() => setIsDeleteRewardConfirming(true)} disabled={selectedRewards.length === 0} className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Trash2 size={16} className="mr-2" /> Delete
              </button>
              <button onClick={() => handleOpenModal('RatioReward')} disabled={selectedRewards.length !== 1} className="bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Ratio size={16} className="mr-2" /> R Ratio
              </button>
              <button onClick={() => handleOpenModal('ColorReward')} disabled={selectedRewards.length !== 1} className="bg-purple-500 text-white py-2 px-4 rounded-lg hover:bg-purple-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Palette size={16} className="mr-2" /> Color
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <input type="text" placeholder="Nama Reward" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
              <select className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option>-Status-</option> <option>Enabled</option> <option>Disabled</option>
              </select>
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold">
                    <input type="checkbox" checked={selectedRewards.length === rewardData.length && rewardData.length > 0} onChange={(e) => handleSelectAll(e, 'reward')} className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                  </th>
                  <th className="py-3 px-4 text-left font-semibold">No</th>
                  <th className="py-3 px-4 text-left font-semibold">Nama Reward</th>
                  <th className="py-3 px-4 text-left font-semibold">Win/Lose</th>
                  <th className="py-3 px-4 text-left font-semibold">Status</th>
                  <th className="py-3 px-4 text-left font-semibold">Ratio</th>
                  <th className="py-3 px-4 text-left font-semibold">Kuota</th>
                  <th className="py-3 px-4 text-left font-semibold">Background</th>
                  <th className="py-3 px-4 text-left font-semibold">Color</th>
                  <th className="py-3 px-4 text-left font-semibold">Keterangan</th>
                  <th className="py-3 px-4 text-left font-semibold">Operator</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {rewardData.length > 0 ? (
                  rewardData.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowSelect(row.id, 'reward')}
                      className={`border-b border-slate-200 transition-colors duration-200 cursor-pointer ${
                        selectedRewards.includes(row.id)
                          ? 'bg-blue-100 dark:bg-blue-900'
                          : 'hover:bg-slate-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <td className="py-2 px-4">
                        <input type="checkbox" checked={selectedRewards.includes(row.id)} readOnly className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                      </td>
                      <td className="py-2 px-4">{row.no}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.name}</td>
                      <td className="py-2 px-4">
                        <span className={`text-sm font-medium px-2.5 py-0.5 rounded ${row.winLose === 'Win' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'}`}>
                          {row.winLose}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        <span className={`text-sm font-medium px-2.5 py-0.5 rounded ${row.status === 'Enabled' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-right">{row.ratio}</td>
                      <td className="py-2 px-4 text-right">{row.kuota}</td>
                      <td className="py-2 px-4">
                        <div style={{ backgroundColor: row.background, width: '20px', height: '20px', borderRadius: '4px' }}></div>
                      </td>
                      <td className="py-2 px-4">
                        <div style={{ backgroundColor: row.color, width: '20px', height: '20px', borderRadius: '4px' }}></div>
                      </td>
                      <td className="py-2 px-4">{row.keterangan}</td>
                      <td className="py-2 px-4 text-xs whitespace-nowrap">{row.operator}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(rewardData, rewardData.length)}
        </div>
      );
    };

    const cronTabContent = () => {
      const handleSyncClick = () => {
        setOpenSyncModal(true);
      };

      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSyncClick}
                className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200"
              >
                <RefreshCcw size={16} className="inline mr-2" /> Sinkron
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-12" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
              <div className="relative w-full md:w-auto">
                <input type="text" placeholder="Search..." className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 w-full" />
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-400" />
              </div>
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                  <th className="py-3 px-4 text-left font-semibold">Start</th>
                  <th className="py-3 px-4 text-left font-semibold">End</th>
                  <th className="py-3 px-4 text-left font-semibold">Durasi</th>
                  <th className="py-3 px-4 text-left font-semibold">Type</th>
                  <th className="py-3 px-4 text-left font-semibold">Sync</th>
                  <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="7" className="py-16 text-center text-slate-400">
                    No data found.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {commonPagination([], 0)}
        </div>
      );
    };

    const rekapTransaksiTabContent = () => {
        return (
          <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0 md:space-x-4 mb-4">
              <div className="flex items-center space-x-2">
                {/* No buttons */}
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
                <input type="text" placeholder="Member ID" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-01" />
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
                <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                  <Search size={16} className="inline mr-2" /> Search
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
              <table className="min-w-full bg-white dark:bg-gray-800">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                    <th className="py-3 px-4 text-left font-semibold">Tanggal</th>
                    <th className="py-3 px-4 text-left font-semibold">Member</th>
                    <th className="py-3 px-4 text-left font-semibold">Deposit</th>
                    <th className="py-3 px-4 text-left font-semibold">Withdraw</th>
                    <th className="py-3 px-4 text-left font-semibold">Spin</th>
                    <th className="py-3 px-4 text-left font-semibold">Credit</th>
                    <th className="py-3 px-4 text-left font-semibold">Voucher In</th>
                    <th className="py-3 px-4 text-left font-semibold">Voucher Out</th>
                    <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Voucher</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700 dark:text-gray-200">
                  {rekapTransaksiData.length > 0 ? (
                    rekapTransaksiData.map((row) => (
                      <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                        <td className="py-2 px-4">{row.no}</td>
                        <td className="py-2 px-4 whitespace-nowrap">{row.tanggal}</td>
                        <td className="py-2 px-4">{row.member}</td>
                        <td className="py-2 px-4 text-right">{row.deposit}</td>
                        <td className="py-2 px-4 text-right">{row.withdraw}</td>
                        <td className="py-2 px-4 text-right">{row.spin}</td>
                        <td className="py-2 px-4 text-right">{row.credit}</td>
                        <td className="py-2 px-4 text-right">{row.voucherIn}</td>
                        <td className="py-2 px-4 text-right">{row.voucherOut}</td>
                        <td className="py-2 px-4 text-right">{row.voucher}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="py-16 text-center text-slate-400">
                        No data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {commonPagination(rekapTransaksiData, 1)}
          </div>
        );
    };

    const rekapMemberTabContent = () => {
        return (
          <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0 md:space-x-4 mb-4">
              <div className="flex items-center space-x-2">
                {/* No buttons */}
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
                <input type="text" placeholder="Member ID" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-01" />
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
                <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                  <Search size={16} className="inline mr-2" /> Search
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
              <table className="min-w-full bg-white dark:bg-gray-800">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                    <th className="py-3 px-4 text-left font-semibold">Tanggal</th>
                    <th className="py-3 px-4 text-left font-semibold">Member ID</th>
                    <th className="py-3 px-4 text-left font-semibold">Deposit</th>
                    <th className="py-3 px-4 text-left font-semibold">Withdraw</th>
                    <th className="py-3 px-4 text-left font-semibold">Spin</th>
                    <th className="py-3 px-4 text-left font-semibold">Credit</th>
                    <th className="py-3 px-4 text-left font-semibold">Voucher In</th>
                    <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Voucher Out</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700 dark:text-gray-200">
                  {rekapMemberData.length > 0 ? (
                    rekapMemberData.map((row) => (
                      <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                        <td className="py-2 px-4">{row.no}</td>
                        <td className="py-2 px-4 whitespace-nowrap">{row.tanggal}</td>
                        <td className="py-2 px-4">{row.memberId}</td>
                        <td className="py-2 px-4 text-right">{row.deposit}</td>
                        <td className="py-2 px-4 text-right">{row.withdraw}</td>
                        <td className="py-2 px-4 text-right">{row.spin}</td>
                        <td className="py-2 px-4 text-right">{row.credit}</td>
                        <td className="py-2 px-4 text-right">{row.voucherIn}</td>
                        <td className="py-2 px-4 text-right">{row.voucherOut}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="py-16 text-center text-slate-400">
                        No data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {commonPagination(rekapMemberData, 100)}
          </div>
        );
    };

    const rekapRewardTabContent = () => {
      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex items-center space-x-2">
              {/* No buttons */}
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <select className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option>-REWARD-</option>
              </select>
              <input type="text" placeholder="Member ID" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-01" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                  <th className="py-3 px-4 text-left font-semibold">Tanggal</th>
                  <th className="py-3 px-4 text-left font-semibold">Reward</th>
                  <th className="py-3 px-4 text-left font-semibold">Member ID</th>
                  <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Min</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {rekapRewardData.length > 0 ? (
                  rekapRewardData.map((row) => (
                    <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                      <td className="py-2 px-4">{row.no}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.tanggal}</td>
                      <td className="py-2 px-4">{row.reward}</td>
                      <td className="py-2 px-4">{row.memberId}</td>
                      <td className="py-2 px-4 text-right">{row.win}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(rekapRewardData, 2)}
        </div>
      );
    };

    const rekapOperatorTabContent = () => {
      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex items-center space-x-2">
              {/* No buttons */}
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <input type="text" placeholder="Operator" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-01" />
              <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                  <th className="py-3 px-4 text-left font-semibold">Tanggal</th>
                  <th className="py-3 px-4 text-left font-semibold">Operator</th>
                  <th className="py-3 px-4 text-left font-semibold">Deposit</th>
                  <th className="py-3 px-4 text-left font-semibold">Withdraw</th>
                  <th className="py-3 px-4 text-left font-semibold">Credit In</th>
                  <th className="py-3 px-4 text-left font-semibold">Voucher In</th>
                  <th className="py-3 px-4 text-left font-semibold">Voucher Cancelled</th>
                  <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Voucher</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {rekapOperatorData.length > 0 ? (
                  rekapOperatorData.map((row) => (
                    <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                      <td className="py-2 px-4">{row.no}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.tanggal}</td>
                      <td className="py-2 px-4">{row.operator}</td>
                      <td className="py-2 px-4 text-right">{row.deposit}</td>
                      <td className="py-2 px-4 text-right">{row.withdraw}</td>
                      <td className="py-2 px-4 text-right">{row.creditIn}</td>
                      <td className="py-2 px-4 text-right">{row.voucherIn}</td>
                      <td className="py-2 px-4 text-right">{row.voucherCancelled}</td>
                      <td className="py-2 px-4 text-right">{row.voucher}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(rekapOperatorData, 2)}
        </div>
      );
    };

    const userLevelTabContent = () => {
      const handleUserLevelFormSubmit = (formData) => {
        const isEditMode = selectedUserLevels.length === 1;
        const updatedData = isEditMode
          ? userLevelData.map(level => level.id === selectedUserLevels[0] ? { ...level, ...formData } : level)
          : [...userLevelData, { ...formData, id: userLevelData.length + 1, no: userLevelData.length + 1, operator: '18/08/2025 07:50:25 AM by Seeder' }];
        setUserLevelData(updatedData);
        handleModalClose();
      };
      
      const handleDeleteUserLevels = () => {
        const updatedData = userLevelData.filter(level => !selectedUserLevels.includes(level.id));
        setUserLevelData(updatedData);
        handleModalClose();
        alert(`Successfully deleted ${selectedUserLevels.length} user level(s).`);
      };

      const UserLevelPermissionsModal = () => {
        const isEditMode = selectedUserLevels.length === 1;
        const [formData, setFormData] = useState({
          namaLevel: isEditMode ? userLevelData.find(l => l.id === selectedUserLevels[0])?.namaLevel : '',
          keterangan: isEditMode ? userLevelData.find(l => l.id === selectedUserLevels[0])?.keterangan : '',
          permissions: {
            Master: { Member: ['Create', 'Edit', 'Password', 'Deposit', 'Withdraw', 'Voucher', 'Cancel'], Voucher: ['Create', 'Edit'], Credit: ['Read'], Reward: ['Create', 'Edit', 'Delete', 'Ratio', 'Color'], Kuota: ['Edit'], Transaksi: ['Read'], Cron: ['Edit'] },
            Laporan: { RekapTransaksi: ['Read'], RekapMember: ['Read'], RekapReward: ['Read'], RekapOperator: ['Read'] },
            Administrasi: { UserLevel: ['Create', 'Edit', 'Delete'], UserManager: ['Create', 'Edit', 'Logout', 'Login History'], UserLog: ['Read'] }
          }
        });
      
        const handleTogglePermission = (section, item, permission) => {
          setFormData(prev => {
            const newPermissions = { ...prev.permissions };
            const currentPermissions = newPermissions[section]?.[item] || [];
            if (currentPermissions.includes(permission)) {
              newPermissions[section][item] = currentPermissions.filter(p => p !== permission);
            } else {
              newPermissions[section][item] = [...currentPermissions, permission];
            }
            return { ...prev, permissions: newPermissions };
          });
        };

        const renderPermissions = (permissions, section) => {
          return Object.keys(permissions).map(item => (
            <div key={item} className="space-y-2">
              <div className="font-bold">{item}</div>
              <ul className="list-none space-y-1 pl-4 border-l-2 border-slate-200 dark:border-gray-600">
                {permissions[item].map(permission => (
                  <li key={permission} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.permissions[section]?.[item]?.includes(permission)}
                      onChange={() => handleTogglePermission(section, item, permission)}
                      className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <span>{permission}</span>
                  </li>
                ))}
              </ul>
            </div>
          ));
        };

        const handleSubmit = (e) => {
          e.preventDefault();
          // Logic to handle form submission with permissions
          handleUserLevelFormSubmit(formData);
        };

        const isDeleteModalOpen = openModal === 'DeleteUserLevel';
        if (isDeleteModalOpen) return null;

        return (
          <Modal isOpen={openModal === 'CreateUserLevel' || (openModal === 'EditUserLevel' && selectedUserLevels.length === 1)} onClose={handleModalClose} title={isEditMode ? 'Edit User Level' : 'Create User Level'}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Nama Level</label>
                <input type="text" name="namaLevel" value={formData.namaLevel || ''} onChange={(e) => setFormData(prev => ({ ...prev, namaLevel: e.target.value }))} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
              </div>
              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-2">Master</h3>
                  {renderPermissions(menuItems.find(m => m.name === 'Master').subItems.reduce((acc, curr) => ({ ...acc, [curr.name]: ['Create', 'Edit', 'Password', 'Deposit', 'Withdraw', 'Voucher', 'Cancel'] }), {}), 'Master')}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-2">Laporan</h3>
                  {renderPermissions(menuItems.find(m => m.name === 'Laporan').subItems.reduce((acc, curr) => ({ ...acc, [curr.name]: ['Read'] }), {}), 'Laporan')}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg mb-2">Administrasi</h3>
                  {renderPermissions(menuItems.find(m => m.name === 'Administrasi').subItems.reduce((acc, curr) => ({ ...acc, [curr.name]: ['Create', 'Edit', 'Delete', 'Logout', 'Login History'] }), {}), 'Administrasi')}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Keterangan</label>
                <textarea name="keterangan" value={formData.keterangan || ''} onChange={(e) => setFormData(prev => ({ ...prev, keterangan: e.target.value }))} rows="3" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"></textarea>
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                  Cancel
                </button>
                <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
                  {isEditMode ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </Modal>
        );
      };

      const DeleteUserLevelConfirmationModal = () => {
        return (
          <Modal isOpen={openModal === 'DeleteUserLevel'} onClose={handleModalClose} title="Delete User Level">
            <div className="text-center">
              <Trash2 size={48} className="mx-auto text-red-500 mb-4" />
              <p className="text-slate-700 dark:text-gray-300 mb-6">Are you sure you want to delete {selectedUserLevels.length} user level(s)? This action cannot be undone.</p>
              <div className="flex justify-center space-x-4">
                <button onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                  No
                </button>
                <button onClick={handleDeleteUserLevels} className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors">
                  Yes, Delete
                </button>
              </div>
            </div>
          </Modal>
        );
      };

      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setOpenModal('CreateUserLevel')} className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center">
                <Plus size={16} className="mr-2" /> Create
              </button>
              <button onClick={() => setOpenModal('EditUserLevel')} disabled={selectedUserLevels.length !== 1} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Pencil size={16} className="mr-2" /> Edit
              </button>
              <button onClick={() => setOpenModal('DeleteUserLevel')} disabled={selectedUserLevels.length === 0} className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Trash2 size={16} className="mr-2" /> Delete
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <button onClick={() => {}} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <RefreshCcw size={16} className="inline mr-2" /> Reload
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold">
                    <input type="checkbox" checked={selectedUserLevels.length === userLevelData.length && userLevelData.length > 0} onChange={(e) => handleSelectAll(e, 'userLevel')} className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                  </th>
                  <th className="py-3 px-4 text-left font-semibold">No</th>
                  <th className="py-3 px-4 text-left font-semibold">Nama Level</th>
                  <th className="py-3 px-4 text-left font-semibold">Keterangan</th>
                  <th className="py-3 px-4 text-left font-semibold">Operator</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {userLevelData.length > 0 ? (
                  userLevelData.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowSelect(row.id, 'userLevel')}
                      className={`border-b border-slate-200 transition-colors duration-200 cursor-pointer ${
                        selectedUserLevels.includes(row.id)
                          ? 'bg-blue-100 dark:bg-blue-900'
                          : 'hover:bg-slate-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <td className="py-2 px-4">
                        <input type="checkbox" checked={selectedUserLevels.includes(row.id)} readOnly className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                      </td>
                      <td className="py-2 px-4">{row.no}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.namaLevel}</td>
                      <td className="py-2 px-4">{row.keterangan}</td>
                      <td className="py-2 px-4 text-xs whitespace-nowrap">{row.operator}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(userLevelData, 1)}
          <UserLevelPermissionsModal />
          <DeleteUserLevelConfirmationModal />
        </div>
      );
    };

    const userManagerTabContent = () => {
      const handleUserManagerFormSubmit = (formData) => {
        const isEditMode = selectedUserManagers.length === 1;
        const updatedData = isEditMode
          ? userManagerData.map(manager => manager.id === selectedUserManagers[0] ? { ...manager, ...formData } : manager)
          : [...userManagerData, { ...formData, id: userManagerData.length + 1, no: userManagerData.length + 1, ipWhitelist: '', login: '' }];
        setUserManagerData(updatedData);
        handleModalClose();
      };
      
      const handleDeleteUserManagers = () => {
        const updatedData = userManagerData.filter(manager => !selectedUserManagers.includes(manager.id));
        setUserManagerData(updatedData);
        handleModalClose();
        alert(`Successfully deleted ${selectedUserManagers.length} user(s).`);
      };

      const UserManagerModal = () => {
        const isEditMode = selectedUserManagers.length === 1;
        const initialManager = isEditMode ? userManagerData.find(manager => manager.id === selectedUserManagers[0]) : {};
        const [formData, setFormData] = useState(initialManager || { status: 'Enabled' });
      
        const handleChange = (e) => {
          const { name, value } = e.target;
          setFormData((prev) => ({ ...prev, [name]: value }));
        };
      
        const handleSubmit = (e) => {
          e.preventDefault();
          handleUserManagerFormSubmit(formData);
        };
      
        return (
          <Modal isOpen={openModal === 'CreateUserManager' || (openModal === 'EditUserManager' && selectedUserManagers.length === 1)} onClose={handleModalClose} title={isEditMode ? 'Edit User' : 'Create User'}>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Username</label>
                <input type="text" name="username" value={formData.username || ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
              </div>
              {!isEditMode && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Password</label>
                  <input type="password" name="password" value={formData.password || ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Nama Lengkap</label>
                <input type="text" name="namaLengkap" value={formData.namaLengkap || ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Level</label>
                <select name="level" value={formData.level || ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200" required>
                  <option value="">-Pilih-</option>
                  <option value="Administrator">Administrator</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Status</label>
                <div className="flex space-x-4 mt-2">
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="status" value="Enabled" checked={formData.status === 'Enabled'} onChange={handleChange} className="form-radio text-blue-500" />
                    <span className="text-slate-700 dark:text-gray-200">Enabled</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="radio" name="status" value="Disabled" checked={formData.status === 'Disabled'} onChange={handleChange} className="form-radio text-blue-500" />
                    <span className="text-slate-700 dark:text-gray-200">Disabled</span>
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">Keterangan</label>
                <textarea name="keterangan" value={formData.keterangan || ''} onChange={handleChange} rows="3" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"></textarea>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-gray-400">IP Whitelist</label>
                <textarea name="ipWhitelist" value={formData.ipWhitelist || ''} onChange={handleChange} rows="3" className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"></textarea>
              </div>
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                  Cancel
                </button>
                <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
                  {isEditMode ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </Modal>
        );
      };
      
      const DeleteUserManagerConfirmationModal = () => {
        return (
          <Modal isOpen={openModal === 'DeleteUserManager'} onClose={handleModalClose} title="Delete User">
            <div className="text-center">
              <Trash2 size={48} className="mx-auto text-red-500 mb-4" />
              <p className="text-slate-700 dark:text-gray-300 mb-6">Are you sure you want to delete {selectedUserManagers.length} user(s)? This action cannot be undone.</p>
              <div className="flex justify-center space-x-4">
                <button onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-6 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                  No
                </button>
                <button onClick={handleDeleteUserManagers} className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 transition-colors">
                  Yes, Delete
                </button>
              </div>
            </div>
          </Modal>
        );
      };

      const LoginHistoryModal = () => {
        return (
          <Modal isOpen={openModal === 'LoginHistory'} onClose={handleModalClose} title="Login History">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0 md:space-x-4 mb-4">
              <div className="flex items-center space-x-2">
                {/* No buttons */}
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-12" />
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
                <input type="text" placeholder="Username" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                  <Search size={16} className="inline mr-2" /> Search
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
              <table className="min-w-full bg-white dark:bg-gray-800">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                    <th className="py-3 px-4 text-left font-semibold">Waktu</th>
                    <th className="py-3 px-4 text-left font-semibold">Username</th>
                    <th className="py-3 px-4 text-left font-semibold">IP Address</th>
                    <th className="py-3 px-4 text-left font-semibold">Platform</th>
                    <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Browser</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700 dark:text-gray-200">
                  {userLogData.length > 0 ? (
                    userLogData.map((row) => (
                      <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                        <td className="py-2 px-4">{row.no}</td>
                        <td className="py-2 px-4 whitespace-nowrap">{row.waktu}</td>
                        <td className="py-2 px-4">{row.username}</td>
                        <td className="py-2 px-4">{row.ipAddress}</td>
                        <td className="py-2 px-4">{row.platform}</td>
                        <td className="py-2 px-4 text-xs">{row.browser}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-16 text-center text-slate-400">
                        No data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {commonPagination(userLogData, 2)}
            <div className="flex justify-end mt-4">
              <button onClick={handleModalClose} className="bg-slate-200 text-slate-700 px-4 py-2 rounded-lg hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">
                Close
              </button>
            </div>
          </Modal>
        );
      };

      return (
        <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 md:space-x-4 mb-4">
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setOpenModal('CreateUserManager')} className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center">
                <Plus size={16} className="mr-2" /> Create
              </button>
              <button onClick={() => setOpenModal('EditUserManager')} disabled={selectedUserManagers.length !== 1} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Pencil size={16} className="mr-2" /> Edit
              </button>
              <button onClick={() => setOpenModal('DeleteUserManager')} disabled={selectedUserManagers.length === 0} className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <Trash2 size={16} className="mr-2" /> Delete
              </button>
              <button onClick={() => {}} disabled={selectedUserManagers.length !== 1} className="bg-slate-200 text-slate-700 py-2 px-4 rounded-lg hover:bg-slate-300 transition-colors duration-200 flex items-center disabled:opacity-50">
                <LogOut size={16} className="mr-2" /> Logout
              </button>
              <button onClick={() => setOpenModal('LoginHistory')} disabled={selectedUserManagers.length !== 1} className="bg-indigo-500 text-white py-2 px-4 rounded-lg hover:bg-indigo-600 transition-colors duration-200 flex items-center disabled:opacity-50">
                <History size={16} className="mr-2" /> Login History
              </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
              <select className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option>-Level-</option>
              </select>
              <select className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                <option>-Status-</option>
              </select>
              <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                <Search size={16} className="inline mr-2" /> Search
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
            <table className="min-w-full bg-white dark:bg-gray-800">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold">
                    <input type="checkbox" checked={selectedUserManagers.length === userManagerData.length && userManagerData.length > 0} onChange={(e) => handleSelectAll(e, 'userManager')} className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                  </th>
                  <th className="py-3 px-4 text-left font-semibold">No</th>
                  <th className="py-3 px-4 text-left font-semibold">Username</th>
                  <th className="py-3 px-4 text-left font-semibold">Nama Lengkap</th>
                  <th className="py-3 px-4 text-left font-semibold">Level</th>
                  <th className="py-3 px-4 text-left font-semibold">Status</th>
                  <th className="py-3 px-4 text-left font-semibold">IP Whitelist</th>
                  <th className="py-3 px-4 text-left font-semibold">Login</th>
                  <th className="py-3 px-4 text-left font-semibold">Aktif</th>
                  <th className="py-3 px-4 text-left font-semibold">Keterangan</th>
                  <th className="py-3 px-4 text-left font-semibold">Operator</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-gray-200">
                {userManagerData.length > 0 ? (
                  userManagerData.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => handleRowSelect(row.id, 'userManager')}
                      className={`border-b border-slate-200 transition-colors duration-200 cursor-pointer ${
                        selectedUserManagers.includes(row.id)
                          ? 'bg-blue-100 dark:bg-blue-900'
                          : 'hover:bg-slate-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <td className="py-2 px-4">
                        <input type="checkbox" checked={selectedUserManagers.includes(row.id)} readOnly className="form-checkbox rounded text-blue-500 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500" />
                      </td>
                      <td className="py-2 px-4">{row.no}</td>
                      <td className="py-2 px-4">{row.username}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.namaLengkap}</td>
                      <td className="py-2 px-4">{row.level}</td>
                      <td className="py-2 px-4">
                        <span className={`text-sm font-medium px-2.5 py-0.5 rounded ${row.status === 'Enabled' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="py-2 px-4">{row.ipWhitelist}</td>
                      <td className="py-2 px-4 whitespace-nowrap">{row.login}</td>
                      <td className="py-2 px-4">
                         <span className={`text-sm font-medium px-2.5 py-0.5 rounded ${row.aktif === 'Enabled' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'}`}>
                          {row.aktif}
                        </span>
                      </td>
                      <td className="py-2 px-4">{row.keterangan}</td>
                      <td className="py-2 px-4 text-xs whitespace-nowrap">{row.operator}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="py-16 text-center text-slate-400">
                      No data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {commonPagination(userManagerData, 1)}
          <UserManagerModal />
          <DeleteUserManagerConfirmationModal />
          <LoginHistoryModal />
        </div>
      );
    };

    const userLogTabContent = () => {
        return (
          <div className="bg-white p-4 rounded-lg shadow-md dark:bg-gray-800">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0 md:space-x-4 mb-4">
              <div className="flex items-center space-x-2">
                {/* No buttons */}
              </div>
              <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 w-full md:w-auto">
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('start')} defaultValue="2025-08-01" />
                <input type="date" className="w-full md:w-auto bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" onChange={handleDateChange('end')} defaultValue="2025-08-18" />
                <input type="text" placeholder="Username" className="w-full md:w-auto pl-4 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                <button onClick={handleSearch} className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200">
                  <Search size={16} className="inline mr-2" /> Search
                </button>
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-slate-300 dark:border-gray-600">
              <table className="min-w-full bg-white dark:bg-gray-800">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold rounded-tl-lg">No</th>
                    <th className="py-3 px-4 text-left font-semibold">Waktu</th>
                    <th className="py-3 px-4 text-left font-semibold">Username</th>
                    <th className="py-3 px-4 text-left font-semibold">IP Address</th>
                    <th className="py-3 px-4 text-left font-semibold">Platform</th>
                    <th className="py-3 px-4 text-left font-semibold rounded-tr-lg">Browser</th>
                  </tr>
                </thead>
                <tbody className="text-slate-700 dark:text-gray-200">
                  {userLogData.length > 0 ? (
                    userLogData.map((row) => (
                      <tr key={row.id} className="border-b border-slate-200 transition-colors duration-200 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700">
                        <td className="py-2 px-4">{row.no}</td>
                        <td className="py-2 px-4 whitespace-nowrap">{row.waktu}</td>
                        <td className="py-2 px-4">{row.username}</td>
                        <td className="py-2 px-4">{row.ipAddress}</td>
                        <td className="py-2 px-4">{row.platform}</td>
                        <td className="py-2 px-4 text-xs">{row.browser}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="py-16 text-center text-slate-400">
                        No data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {commonPagination(userLogData, 2)}
          </div>
        );
    };

    return (
      <main className="flex-1 p-4 md:p-6 bg-slate-100 rounded-r-xl overflow-y-auto dark:bg-gray-700">
        <div className="flex flex-wrap space-x-2 border-b border-slate-300 mb-4 dark:border-gray-600">
          {openTabs.map((tab) => (
            <div
              key={tab}
              className={`py-2 px-2 md:px-4 rounded-t-lg transition-colors duration-200 text-sm md:text-base cursor-pointer flex items-center space-x-2 ${
                activeTab === tab
                  ? 'bg-white text-blue-600 border border-slate-300 border-b-white dark:bg-gray-800 dark:text-blue-400 dark:border-gray-600 dark:border-b-gray-800'
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500'
              }`}
              onClick={() => setActiveTab(tab)}
            >
              <span>{tab}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseTab(tab);
                }}
                className="text-red-500 hover:text-red-700 ml-1"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>

        {activeTab === 'Member' && memberTabContent()}
        {activeTab === 'Voucher' && voucherTabContent()}
        {activeTab === 'Credit' && creditTabContent()}
        {activeTab === 'Reward' && rewardTabContent()}
        {activeTab === 'Cron' && cronTabContent()}
        {activeTab === 'Rekap Transaksi' && rekapTransaksiTabContent()}
        {activeTab === 'Rekap Member' && rekapMemberTabContent()}
        {activeTab === 'Rekap Reward' && rekapRewardTabContent()}
        {activeTab === 'Rekap Operator' && rekapOperatorTabContent()}
        {activeTab === 'User Level' && userLevelTabContent()}
        {activeTab === 'User Manager' && userManagerTabContent()}
        {activeTab === 'User Log' && userLogTabContent()}

        {activeTab === '' && (
          <div className="flex items-center justify-center p-8 md:p-20 bg-white rounded-lg shadow-md dark:bg-gray-800 dark:text-gray-400 text-center">
            <span className="text-sm md:text-lg">
              Pilih menu dari sidebar untuk memulai.
            </span>
          </div>
        )}
      </main>
    );
  };

  return (
    <div className={`flex flex-col md:flex-row h-screen font-sans ${isDarkMode ? 'dark' : ''} bg-slate-200 dark:bg-gray-900`}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <MainContent />
      </div>
      <MemberModal />
      <BatchEditModal />
      <PasswordModal />
      <DepositWithdrawModal type="deposit" />
      <DepositWithdrawModal type="withdraw" />
      <VoucherModal />
      <DeleteConfirmationModal />
      <DeleteRewardConfirmationModal />
      <RewardCreateEditModal />
      <RewardRatioModal />
      <RewardColorModal />
      <SyncModal />
    </div>
  );
};

export default Dashboard;
